import { Validator } from 'fluentvalidation-ts';
import { every, uniqBy } from 'lodash';
import { fieldRequiredValidationMessage } from '../../../validators/sharedValidators';
import { Project } from '../../projects/project-section/ProjectHeader';
import { ThermostatTypeCode } from '../../shared/quote-or-design-form/quoteOrDesignFormData';
import { defaultThermostatTypeCode } from '../../shared/quote-or-design-form/rooms/roomFormModel';
import {
  initialUnitFormModel,
  UnitFormModel,
  UnitFormModelValidator,
  unitOnlyHasManifoldsWithRooms,
  unitOnlyHasRoomlessManifolds,
} from '../../shared/quote-or-design-form/units/unitFormModel';
import {
  haveAtLeastOneUnit,
  haveAtMostOneRadiatorManifoldOnEachUnit,
  haveHeatPumpOnEveryUnitThatHasRadiatorManifold,
} from '../../shared/quote-or-design-form/units/unitValidators';

export type QuoteFormModel = {
  quoteId: number | null;
  useLegacyPricingModel: boolean;
  name: string;
  customerId: number | null;
  isDraft: boolean;
  thermostatTypeCode: ThermostatTypeCode;
  includeNeoHub: boolean;
  units: Array<UnitFormModel>;
  focusedUnitId: string | null;
  // This is for validation errors that relate to the units tree as a whole rather than to individual fields
  unitsValidation: null;
};

export const getInitialQuoteFormModel = (project: Project): QuoteFormModel => {
  const customers = uniqBy([project.customer, ...project.alternativeCustomers], 'customerId');

  return {
    quoteId: null,
    useLegacyPricingModel: false,
    name: '',
    customerId: customers.length === 1 ? customers[0].customerId : null,
    thermostatTypeCode: defaultThermostatTypeCode,
    includeNeoHub: false,
    units: [initialUnitFormModel],
    isDraft: true,
    focusedUnitId: null,
    unitsValidation: null,
  };
};

export class QuoteFormModelValidator extends Validator<QuoteFormModel> {
  constructor() {
    super();

    this.ruleFor('name')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('customerId')
      .notNull()
      .withMessage(fieldRequiredValidationMessage);

    this.ruleForEach('units').setValidator(quote => new UnitFormModelValidator(quote.isDraft, 'Quote'));

    this.ruleFor('unitsValidation')
      .must(haveAtLeastOneUnit)
      .must(haveHeatPumpOnEveryUnitThatHasRadiatorManifold)
      .must(haveAtMostOneRadiatorManifoldOnEachUnit)
      .unless(quote => quote.isDraft);

    this.ruleFor('unitsValidation')
      .must(
        (_, { units }) =>
          every(units, unit => unitOnlyHasRoomlessManifolds(unit)) ||
          every(units, unit => unitOnlyHasManifoldsWithRooms(unit)),
      )
      .withMessage('Either all Manifolds should have Rooms, or no Manifolds should have Rooms.');
  }
}

export const quoteFormModelValidator = new QuoteFormModelValidator();
